import type { TOC } from '@ember/component/template-only';
import { hash } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { PrismButton } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import GeneralCardWithIllustration from 'lumen/components/general/card-with-illustration';
import SettingsPageTemplate from 'lumen/components/settings/page-template';
import type HotelSettingsCompetitorsIndexController from 'lumen/controllers/hotel/settings/competitors/index';

interface HotelSettingsCompetitorsIndexSignature {
  Args: {
    controller: HotelSettingsCompetitorsIndexController;
    model: unknown;
  };
}

<template>
  <SettingsPageTemplate
    @description={{t "settings.competitors.description"}}
    @hasAccountSelector={{true}}
    @title={{t "general.competitors"}}
  >
    <div class="flex flex-col gap-600">
      {{#if
        (or @controller.access.hasAppCompset @controller.access.hasRmsCompset)
      }}
        <section
          class="flex flex-col gap-400"
          data-test-id="competitors-section-general"
        >
          <h3>{{@controller.otherProductsTitle}}</h3>
          <div class="grid grid-cols-3 gap-400">
            <GeneralCardWithIllustration
              @description={{t "settings.competitors.compset.description"}}
              @illustration="40x40_building-hotel"
              @title={{t "settings.competitors.compset"}}
              data-test-id="competitors-card-compset"
            >
              <:footer>
                <div class="flex items-center justify-end">
                  <LinkTo
                    @query={{hash activeTab="compset"}}
                    @route="hotel.settings.competitors.general"
                    tabindex="-1"
                  >
                    <PrismButton
                      @buttonType="secondary"
                      @label={{t "settings.competitors.compset.manage"}}
                    />
                  </LinkTo>
                </div>
              </:footer>
            </GeneralCardWithIllustration>

            {{#unless @controller.access.isPricingAssistantStandaloneVersion}}
              <GeneralCardWithIllustration
                @description={{t
                  "settings.competitors.rms_compset.description"
                }}
                @illustration="40x40_screen-api"
                @title={{t "settings.competitors.RMS_compset"}}
                data-test-id="competitors-card-rms-compset"
              >
                <:footer>
                  <div class="flex items-center justify-end">
                    <LinkTo
                      @query={{hash activeTab="rms"}}
                      @route="hotel.settings.competitors.general"
                      tabindex="-1"
                    >
                      <PrismButton
                        @buttonType="secondary"
                        @label={{t "settings.competitors.rms_compset.manage"}}
                      />
                    </LinkTo>
                  </div>
                </:footer>
              </GeneralCardWithIllustration>
            {{/unless}}
          </div>
        </section>
      {{/if}}

      {{#if @controller.hasAnyCompsetBenchmark}}
        <section
          class="flex flex-col gap-400"
          data-test-id="competitors-section-benchmark"
        >
          <h3>{{t "general.benchmark_insight"}}</h3>
          <div class="grid grid-cols-3 gap-400">
            {{#if @controller.access.hasCompsetBenchmark}}
              <GeneralCardWithIllustration
                @description={{t "settings.competitors.compset.description"}}
                @illustration="40x40_building-hotel"
                @title={{t "settings.competitors.compset"}}
                data-test-id="competitors-card-benchmark-compset"
              >
                <:footer>
                  <div class="flex items-center justify-end">
                    <LinkTo
                      @query={{hash activeTab="compset"}}
                      @route="hotel.settings.competitors.benchmark.compset"
                      tabindex="-1"
                    >
                      <PrismButton
                        @buttonType="secondary"
                        @label={{t "settings.competitors.compset.manage"}}
                      />
                    </LinkTo>
                  </div>
                </:footer>
              </GeneralCardWithIllustration>
            {{/if}}

            {{#if @controller.access.hasSettingsBenchmarkPortfolioCompsetView}}
              <GeneralCardWithIllustration
                @description={{t
                  "settings.competitors.portfolio_compset.description"
                }}
                @illustration="40x40_building-corporate"
                @title={{t "settings.competitors.portfolio_compset"}}
                data-test-id="competitors-card-benchmark-portfolio-compset"
              >
                <:footer>
                  <div class="flex items-center justify-end">
                    <LinkTo
                      @query={{hash activeTab="portfolio_compset"}}
                      @route="hotel.settings.competitors.benchmark"
                      tabindex="-1"
                    >
                      <PrismButton
                        @buttonType="secondary"
                        @label={{t
                          "settings.competitors.portfolio_compset.manage"
                        }}
                      />
                    </LinkTo>
                  </div>
                </:footer>
              </GeneralCardWithIllustration>
            {{/if}}

            {{#if @controller.hasBenchmarkSmartCompset}}
              <GeneralCardWithIllustration
                @description={{t
                  "settings.competitors.smart_compset.description"
                }}
                @illustration="40x40_ai"
                @title={{t "settings.competitors.smart_compset"}}
                data-test-id="competitors-card-benchmark-smart-compset"
              >
                <:footer>
                  <div class="flex items-center justify-end">
                    <LinkTo
                      @query={{hash activeTab="smart_compset"}}
                      @route="hotel.settings.competitors.benchmark"
                      tabindex="-1"
                    >
                      <PrismButton
                        @buttonType="secondary"
                        @label={{t "settings.competitors.smart_compset.manage"}}
                      />
                    </LinkTo>
                  </div>
                </:footer>
              </GeneralCardWithIllustration>
            {{/if}}
          </div>
        </section>
      {{/if}}
    </div>
  </SettingsPageTemplate>
</template> satisfies TOC<HotelSettingsCompetitorsIndexSignature>;
